import { useEffect, useState } from 'react';

import uuid from 'uuid/v1';

import { useNotificationActions } from 'src/app/state/notifications';

import { validateAllRules } from './validator';

export type Rule = {
  Attribute: string | '';
  Condition: string | '';
  Id?: string;
  Operator: string;
  SecondValue?: string | null;
  Unit?: string;
  Value: string;
  Variant?: string | null;
};

export type RuleGroup = {
  combinator: string;
  id?: string;
  rules: Rule[];
};

export type Variant = {
  id: string;
  label: string;
};

export type Attributes = {
  label: string;
  name: string;
  operatorGroup: string;
  unit?: string;
  variants?: Variant[];
};

export type Schema = Record<
  string,
  {
    attributes: Attributes[];
    label: string;
    value: string;
  }
>;

export type UseQueryBuilderProps = {
  isQueryBuilderDirty?: boolean;
  queryRules: RuleGroup;
  schema: Schema;
  setIsQueryBuilderDirty: (isDirty: boolean) => void;
};

export type UseQueryBuilder = {
  addRule: () => void;
  areQueryRulesValid?: boolean;
  removeRule: (id: string) => void;
  ruleGroup: RuleGroup;
  updateCombinator: (combinator: string) => void;
  updateRule: (id: string, newValues: Partial<Rule>) => void;
};

/**
 * This hook manages a dynamic rule group for building complex queries, providing functionalities for adding, updating, and removing rules, as well as handling the combinator.
 * @param {RuleGroup} [props.queryRules] - An optional initial set of rules and combinator to start with.
 * @param {Record<string, unknown>} [props.schema] - Schema definition for the query builder.
 * @returns {Object} An object with methods and state for managing the rule group:
 *   - ruleGroup: The current state of the rule group.
 *   - addRule: Adds a new rule to the group, ensuring the rule limit (5) is not exceeded.
 *   - removeRule: Removes a rule from the group by its ID.
 *   - updateRule: Updates specific attributes of a rule by its ID.
 *   - updateCombinator: Changes the combinator used to combine rules within the group.
 *   - areQueryRulesValid: A boolean indicating is the query rules are valid or not.
 */

const useQueryBuilder = ({ queryRules, schema, setIsQueryBuilderDirty }: UseQueryBuilderProps): UseQueryBuilder => {
  // start with a default
  const [ruleGroup, setRuleGroup] = useState<RuleGroup>({
    combinator: 'and',
    rules: [
      {
        Id: uuid(),
        Condition: '',
        Attribute: '',
        Operator: '',
        Value: '',
        SecondValue: '',
        Variant: '',
      },
    ],
  });
  const notifications = useNotificationActions();

  useEffect(() => {
    if (queryRules) {
      setRuleGroup(queryRules);
    }
  }, [queryRules]);

  // validate all rules on mount
  const areRulesValid = validateAllRules({ ruleGroup: queryRules, schema });
  const [areQueryRulesValid, setAreQueryRulesValid] = useState(areRulesValid);

  const updateCombinator = (newCombinator: string) => {
    setRuleGroup({
      ...ruleGroup,
      combinator: newCombinator,
    });
    setIsQueryBuilderDirty(true);
  };

  const addRule = () => {
    if (ruleGroup.rules.length >= 5) {
      const errorMessage = 'Cannot add more than 5 rules.';
      notifications.alert({ message: errorMessage });
      return;
    }

    const newRule: Rule = {
      Id: uuid(),
      Condition: '',
      Attribute: '',
      Operator: '',
      Value: '',
      SecondValue: '',
      Variant: '',
    };
    const updatedRuleGroup = {
      ...ruleGroup,
      rules: [...ruleGroup.rules, newRule],
    };
    setRuleGroup(updatedRuleGroup);
    const areRulesValid = validateAllRules({ ruleGroup: updatedRuleGroup, schema });
    setAreQueryRulesValid(areRulesValid);
  };

  const removeRule = (id: string) => {
    const updatedRuleGroup = {
      ...ruleGroup,
      rules: ruleGroup.rules.filter((rule) => rule.Id !== id),
    };
    setRuleGroup(updatedRuleGroup);
    const areRulesValid = validateAllRules({ ruleGroup: updatedRuleGroup, schema });
    setAreQueryRulesValid(areRulesValid);

    // removing a rule makes the form dirty
    setIsQueryBuilderDirty(true);
  };

  const updateRule = (id: string, newValues: Partial<Rule>) => {
    const updatedRuleGroup = {
      ...ruleGroup,
      rules: ruleGroup.rules.map((rule) => (rule.Id === id ? { ...rule, ...newValues } : rule)),
    };

    const updatedRule = updatedRuleGroup.rules.find((rule) => rule.Id === id);

    if (updatedRule) {
      const areRulesValid = validateAllRules({ ruleGroup: updatedRuleGroup, schema });
      setRuleGroup(updatedRuleGroup);
      setIsQueryBuilderDirty(true);
      setAreQueryRulesValid(areRulesValid);
    }
  };

  return {
    ruleGroup,
    updateCombinator,
    addRule,
    removeRule,
    updateRule,
    areQueryRulesValid,
  };
};

export default useQueryBuilder;
